export type LaunchReservationReason = 'open' | 'draft' | 'closed' | 'unknown';

export type LaunchReservationGate = {
  allowed: boolean;
  reason: LaunchReservationReason;
};

export type LaunchActionErrorKey =
  | 'errors.notOpen'
  | 'errors.conflict'
  | 'errors.generic';

/**
 * Whether unit reservations are allowed for a launch lifecycle status.
 *
 * @param status - Launch status from the board payload
 * @returns Gate result with the blocking reason when reservations are closed
 */
export function launchReservationGate(
  status: string | null | undefined,
): LaunchReservationGate {
  const value = (status ?? '').toLowerCase();
  if (value === 'open') return { allowed: true, reason: 'open' };
  if (value === 'draft') return { allowed: false, reason: 'draft' };
  if (value === 'closed') return { allowed: false, reason: 'closed' };
  return { allowed: false, reason: 'unknown' };
}

/**
 * Flattens an API error into searchable text.
 *
 * @param error - Thrown value from a launch mutation
 * @returns Concatenated title, detail, and message
 */
function errorBlob(error: unknown): string {
  if (!error || typeof error !== 'object') return '';
  const rec = error as {
    body?: { title?: string; detail?: string };
    message?: string;
  };
  return `${rec.body?.title ?? ''} ${rec.body?.detail ?? ''} ${rec.message ?? ''}`;
}

/**
 * Maps a failed launch mutation to an i18n key the board can toast.
 *
 * @param error - Thrown value from a launch mutation
 * @returns features/launches error key
 */
export function launchActionErrorKey(error: unknown): LaunchActionErrorKey {
  if (/LaunchNotOpen/i.test(errorBlob(error))) return 'errors.notOpen';
  const status = (error as { status?: number } | null)?.status;
  if (status === 409) return 'errors.conflict';
  return 'errors.generic';
}
